// FAQ data structure with all questions and answers organized by category based on the comprehensive markdown documentation
export const faqData = {
  "getting-started": {
    title: "Getting Started",
    description:
      "Learn the basics of Artifact and how it differs from other AI platforms",
    questions: [
      {
        question:
          "What is Artifact and how is it different from other AI chat platforms?",
        answer:
          "Artifact is an AI-native workspace that combines multiple AI models (ChatGPT, Claude, Gemini, Mistral, and more) in a single platform with collaborative features, shared workspaces, and intelligent document management.",
      },
      {
        question:
          "How do I sign up for Artifact and what's included in the free trial?",
        answer:
          "New users receive a 14-day free trial of the Pro plan, giving full access to all features to help establish utility before converting to a paid subscription.",
      },
      {
        question: "Which AI models are available in Artifact?",
        answer:
          "Artifact integrates with 30+ leading AI models including OpenAI (GPT-4o), Anthropic (Claude Opus 4), Google (Gemini 2.5 Pro), Mistral AI, Groq, DeepSeek, Perplexity, Cohere, and uncensored models like Dolphin.",
      },
      {
        question: "What are the system requirements for using Artifact?",
        answer:
          "Artifact is a web-based platform accessible via browser, with iOS app launching in Q1 2026 and Chrome extension coming shortly thereafter.",
      },
      {
        question:
          "How does Artifact's unified workspace improve team productivity?",
        answer:
          "By consolidating multiple AI tools into one adaptive platform, eliminating the need for subscription sprawl and reducing context switching between different services.",
      },
    ],
  },
  "using-artifact": {
    title: "Using Artifact",
    description:
      "Master AI model switching, Thread Memory, and conversation features",
    questions: [
      {
        question:
          "How do I switch between different AI models during a conversation?",
        answer:
          "You can seamlessly switch between any of the integrated AI models within the same conversation thread by clicking on the model dropdown and selecting your preferred model to leverage each model's unique strengths for different tasks.",
      },
      {
        question: "What is Thread Memory and how does it maintain context?",
        answer:
          "Thread Memory maintains context within conversations, allowing the AI to remember previous interactions and provide more coherent, contextual responses throughout your discussion.",
      },
      {
        question: "Can I use multiple AI models simultaneously for comparison?",
        answer:
          "Yes, Artifact allows you to query multiple models at once to compare responses and choose the best output for your specific needs by hovering on your message and clicking on the retry button then choosing which model to get another response from.",
      },
      {
        question:
          "How do I create AI-generated titles for my conversation threads?",
        answer:
          "Artifact automatically generates smart titles for your threads based on the conversation content, helping you organize and find discussions easily.",
      },
      {
        question:
          "What are the token limits for different AI models in Artifact?",
        answer:
          "Token limits vary by model - Claude offers up to 200K tokens, Gemini 2.5 Pro extends to 2M tokens, and other models have their specific context windows detailed in the platform.",
      },
    ],
  },
  "documents-files": {
    title: "Documents & Files",
    description:
      "Upload, process, and analyze files with Vector Database integration",
    questions: [
      {
        question: "What file types can I upload and process in Artifact?",
        answer:
          "Artifact supports multiple file types including PDFs, CSVs, images, text documents, and more, with multi-modal understanding capabilities across different AI models.",
      },
      {
        question:
          "How does the Vector Database integration work for document retrieval?",
        answer:
          "Documents are automatically converted into searchable vectors, enabling semantic search based on meaning rather than just keywords, with contextual retrieval during conversations.",
      },
      {
        question: "Can I share documents across team projects?",
        answer:
          "Yes, documents uploaded to shared projects are accessible to all team members with appropriate permissions, facilitating knowledge sharing and collaboration.",
      },
      {
        question: "How much storage is available for documents in each plan?",
        answer:
          "Storage limits vary by subscription tier - specific limits will be detailed in your plan settings (usage-based pricing applies after reaching plan limits).",
      },
      {
        question: "How does Artifact process and analyze large datasets?",
        answer:
          "Artifact uses advanced AI models with specialized data analysis capabilities to process CSVs, visualize data, and provide intelligent insights from your datasets.",
      },
    ],
  },
  projects: {
    title: "Projects",
    description:
      "Create collaborative workspaces with shared memory and team access",
    questions: [
      {
        question: "What is a Project and how do I create one?",
        answer:
          "Projects are collaborative environments where teams can share conversations, documents, and AI interactions. You can create projects from your dashboard by clicking the plus sign next to projects and selecting a personal or team project to organize different projects or teams.",
      },
      {
        question: "How many team members can I add to a Project?",
        answer:
          "Team member limits depend on your subscription plan - Team plans allow multiple collaborators with granular access control and user management features.",
      },
      {
        question:
          "Can I have different permissions for team members in a Project?",
        answer:
          "Yes, Artifact provides granular control over project access, allowing you to set different permission levels for team members based on their roles.",
      },
      {
        question: "How does Project Memory differ from Thread Memory?",
        answer:
          "Project Memory shares knowledge across all team members in a project, while Thread Memory maintains context within individual conversations.",
      },
      {
        question: "Can I transfer or export data from one Project to another?",
        answer:
          "Project data management features allow you to organize and potentially migrate content between projects by exporting all data and then uploading that as context.",
      },
    ],
  },
  tools: {
    title: "Tools",
    description:
      "Deep Research, Web Search, code generation, and third-party integrations",
    questions: [
      {
        question: "What is Deep Research and when should I use it?",
        answer:
          "Deep Research conducts comprehensive analysis on complex topics using multiple sources and AI models, scraping entire sites and navigating to sub-links, ideal for in-depth reports or thorough investigations.",
      },
      {
        question: "How does the Web Search integration work?",
        answer:
          "Web Search finds real-time information across the internet using our custom web search to get better search results, with the option to also use Perplexity's Sonar model for web search which provides search-grounded responses with automatic source citations.",
      },
      {
        question: "Can I generate and debug code with Artifact?",
        answer:
          "Yes, multiple AI models excel at code generation, explanation, and debugging, with Claude achieving state-of-the-art performance on real-world software issues.",
      },
      {
        question: "What third-party integrations are available?",
        answer:
          "Artifact supports integrations with GitHub, Slack, Google Drive, Gmail, and LinkedIn, with more integrations planned.",
      },
      {
        question: "How do I access and use the available tools?",
        answer:
          "Tools are accessible through the interface and can be used within conversations to enhance your AI interactions with specialized capabilities for research, coding, and data analysis.",
      },
    ],
  },
  "subscription-plans": {
    title: "Subscription Plans",
    description: "Understand pricing, usage limits, and billing options",
    questions: [
      {
        question: "What subscription plans does Artifact offer?",
        answer:
          "Artifact offers monthly and yearly subscription plans with usage-based pricing after plan limits are reached, including individual Pro plans and Team plans for collaboration.",
      },
      {
        question: "What happens when I exceed my plan's usage limits?",
        answer:
          "After reaching your plan's included limits, usage-based pricing applies automatically, allowing you to continue using Artifact without interruption.",
      },
      {
        question: "Can I switch between monthly and yearly billing?",
        answer:
          "Yes, you can switch between monthly and yearly billing cycles, with yearly plans typically offering cost savings compared to monthly subscriptions.",
      },
      {
        question: "Is there a free plan available after the trial ends?",
        answer:
          "Specific free tier details will be available at launch - the 14-day trial gives you time to evaluate which paid plan best suits your needs.",
      },
      {
        question: "How does team billing work for collaborative projects?",
        answer:
          "Team plans are billed based on the number of seats/users, with consolidated billing for all team members under a single subscription.",
      },
    ],
  },
  troubleshooting: {
    title: "Troubleshooting",
    description:
      "Resolve issues with documents, collaboration, and AI responses",
    questions: [
      {
        question:
          "My documents aren't appearing in the vector database search - what should I do?",
        answer:
          "Allow time for indexing to complete, ensure files are in supported formats, and check that documents are properly uploaded to the project.",
      },
      {
        question:
          "How do I resolve issues with real-time collaboration features?",
        answer:
          "Check your internet connection, refresh the browser, ensure you're using a supported browser version, and verify project permissions are correctly set.",
      },
      {
        question: "What should I do if AI responses are cut off or incomplete?",
        answer:
          "This may be due to token limits - try using a model with higher context capacity or break your request into smaller segments.",
      },
      {
        question: "How can I report bugs or technical issues?",
        answer:
          "Use the in-app feedback system or contact support through the help center for technical assistance and bug reporting.",
      },
    ],
  },
  "account-management": {
    title: "Account Management",
    description: "Manage billing, security, notifications, and data settings",
    questions: [
      {
        question: "How do I update my billing information or payment method?",
        answer:
          "Access your account settings to update billing information, change payment methods, or modify subscription details.",
      },
      {
        question: "Can I delete my account and what happens to my data?",
        answer:
          "Yes, you can delete your account through account settings - your data will be removed according to our data retention policy.",
      },
      {
        question:
          "How do I manage email notifications and communication preferences?",
        answer:
          "Customize notification settings in your account preferences to control email alerts for project activity, billing, and platform updates.",
      },
      {
        question:
          "Is two-factor authentication available for account security?",
        answer:
          "Account security features including authentication options are managed through the Supabase integration - check security settings in your account.",
      },
      {
        question: "How do I transfer project ownership to another team member?",
        answer:
          "Project ownership can be transferred through project settings by current owners with appropriate administrative privileges.",
      },
    ],
  },
  "security-privacy": {
    title: "Security & Privacy",
    description:
      "Data protection, encryption, compliance, and project isolation",
    questions: [
      {
        question:
          "How does Artifact protect my data when using multiple AI models?",
        answer:
          "Artifact implements enterprise-grade encryption for all data transmissions between AI providers, with no data persistence across sessions and strict access controls for project content.",
      },
      {
        question: "Are my conversations with AI models private and encrypted?",
        answer:
          "Yes, all conversations are encrypted end-to-end, stored securely in our vector database, and never shared between projects without explicit permission.",
      },
      {
        question: "What happens to my uploaded documents and files?",
        answer:
          "Documents are processed into secure vector embeddings for intelligent retrieval, with original files encrypted and stored according to your project retention settings.",
      },
      {
        question:
          "Does Artifact comply with data protection regulations like GDPR?",
        answer:
          "Artifact maintains compliance with major data protection regulations, offering data export, deletion rights, and transparent data processing policies.",
      },
      {
        question: "Can AI models access my data from other projects?",
        answer:
          "No, each project is completely isolated with its own vector database and memory system - AI models can only access data within the current project context.",
      },
    ],
  },
  "ai-model-comparisons": {
    title: "AI Model Comparisons",
    description:
      "Choose the right model for coding, context, speed, and specific tasks",
    questions: [
      {
        question: "Which AI model should I use for coding tasks?",
        answer:
          "Claude excels at complex coding with state-of-the-art performance on real-world software issues, while GPT-4o is excellent for code generation and GPT-4.5-preview for general programming tasks.",
      },
      {
        question:
          "What's the difference between Gemini's context windows and other models?",
        answer:
          "Gemini 2.5 Pro offers industry-leading 2M token context windows compared to Claude's 200K tokens and standard models' smaller windows, making it ideal for analyzing extensive documents.",
      },
      {
        question: "Which models are best for real-time applications?",
        answer:
          "Groq's LPU delivers 480 tokens/second for ultra-fast inference, while Gemini Flash and DeepSeek Fast modes offer balanced speed and quality for real-time needs.",
      },
      {
        question: "How do I choose between censored and uncensored models?",
        answer:
          "Standard models (GPT, Claude, Gemini) include safety filters suitable for most business use cases, while Dolphin models provide unrestricted responses for research or creative projects requiring unfiltered output.",
      },
      {
        question: "Which AI model offers the best value for high-volume usage?",
        answer:
          "Mistral and Cohere models offer competitive pricing for enterprise use, while DeepSeek provides cost-efficient deployment - your usage patterns determine the most economical choice.",
      },
    ],
  },
  "integrations-api": {
    title: "Integrations & API",
    description:
      "Connect with Slack, Google Drive, GitHub, and programmatic access",
    questions: [
      {
        question:
          "How do I connect Artifact to my existing tools like Slack or Google Drive?",
        answer:
          "Navigate to the integration settings in your account to connect with supported services including GitHub, Slack, Google Drive, Gmail, and LinkedIn.",
      },
      {
        question: "Is there an API for programmatic access to Artifact?",
        answer:
          "API access is planned for enterprise customers, allowing programmatic interaction with AI models and project data through RESTful endpoints.",
      },
      {
        question: "Can I use Artifact with my company's SSO system?",
        answer:
          "Enterprise plans support SAML-based SSO integration through our Supabase authentication system for seamless corporate access management.",
      },
      {
        question: "What file formats are supported for import/export via API?",
        answer:
          "Artifact supports JSON, CSV, PDF, DOCX, and standard image formats for import/export, with automatic conversion between formats where applicable.",
      },
    ],
  },
  "data-management-export": {
    title: "Data Management & Export",
    description: "Export projects, backup data, and manage retention policies",
    questions: [
      {
        question:
          "Can I export all my project data if I decide to leave Artifact?",
        answer:
          "Yes, you can export all conversations, documents, and project data in standard formats (JSON, CSV, PDF) through your account settings at any time.",
      },
      {
        question: "How long is my data retained after deleting a project?",
        answer: "Deleted project data is permanently removed immediately.",
      },
      {
        question: "Can I migrate data between different projects?",
        answer:
          "Project admins can selectively export and import conversations, documents, and settings between projects they manage.",
      },
      {
        question:
          "What happens to my vector embeddings if I cancel my subscription?",
        answer:
          "Vector embeddings are deleted along with your project data according to our retention policy, with export options available before cancellation.",
      },
      {
        question: "How do I backup my Artifact project data?",
        answer:
          "Automated backups occur daily for Pro and Team plans, with manual backup triggers and downloadable archives available in settings.",
      },
    ],
  },
  "performance-limitations": {
    title: "Performance & Limitations",
    description:
      "Rate limits, file sizes, concurrent sessions, and optimization",
    questions: [
      {
        question: "What are the rate limits for different AI models?",
        answer:
          "Rate limits vary by model and plan - Pro users typically get 1000 requests/hour for standard models, with higher limits for Team and Enterprise plans.",
      },
      {
        question: "Why do some AI responses take longer than others?",
        answer:
          "Response time depends on model complexity, context length, and current load - Groq offers fastest responses while models like GPT-4o and Claude Opus may take longer for complex tasks.",
      },
      {
        question: "Is there a maximum file size for document uploads?",
        answer:
          "Individual files are limited to 100MB for Pro plans and 500MB for Team plans, with total project storage limits based on your subscription tier.",
      },
      {
        question: "How many concurrent AI conversations can I have?",
        answer:
          "Pro plans support up to 5 concurrent conversations across different models, while Team plans allow unlimited concurrent sessions for all team members.",
      },
      {
        question: "What happens if I exceed my monthly token limit?",
        answer:
          "Usage-based pricing automatically applies after reaching plan limits, with real-time usage tracking and alerts to help manage consumption.",
      },
    ],
  },
  "use-cases-industries": {
    title: "Use Cases & Industries",
    description:
      "Legal, development, research, and financial services applications",
    questions: [
      {
        question: "How can legal teams use Artifact for contract analysis?",
        answer:
          "Legal teams leverage Claude's 200K token window for contract review, Perplexity for legal research with citations, and collaborative projects for team document analysis.",
      },
      {
        question: "What's the best setup for software development teams?",
        answer:
          "Development teams typically use Claude for debugging, GPT-4o for architecture planning, GitHub integration for code management, and shared projects for documentation.",
      },
      {
        question: "Can research institutions use Artifact for academic work?",
        answer:
          "Researchers utilize DeepSeek for mathematical proofs, Perplexity for literature reviews with citations, long-context models for paper analysis, and team projects for collaboration.",
      },
      {
        question:
          "How do financial services firms ensure compliance while using Artifact?",
        answer:
          "Financial firms implement project-level access controls, use enterprise security features, maintain audit logs, and leverage on-premise deployment options for sensitive data.",
      },
    ],
  },
  "mobile-voice-features": {
    title: "Mobile & Voice Features",
    description: "iOS app, Android plans, voice input, and cross-platform sync",
    questions: [
      {
        question:
          "When will the iOS app be available and what features will it include?",
        answer:
          "The iOS app launches Q1 2026 with full conversation capabilities, document scanning, voice input via Whisper integration, and offline mode for viewing past conversations.",
      },
      {
        question: "Will there be an Android version of Artifact?",
        answer:
          "Android development is planned following iOS launch success, with feature parity expected by Q4 2025.",
      },
      {
        question: "How does voice input work with different AI models?",
        answer:
          "Whisper model integration (July 2025) will convert speech to text for any AI model, with voice-optimized responses for natural conversation flow.",
      },
      {
        question:
          "Can I continue conversations seamlessly between mobile and desktop?",
        answer:
          "Yes, cross-platform synchronization ensures all conversations, documents, and project data sync in real-time across all your devices.",
      },
      {
        question: "Will mobile apps support all AI models and features?",
        answer:
          "Mobile apps will support all AI models with optimized interfaces, though some features like complex data visualization may be enhanced on desktop.",
      },
    ],
  },
  "enterprise-team-features": {
    title: "Enterprise & Team Features",
    description:
      "Permissions, collaborative editing, custom AI agents, and training materials",
    questions: [
      {
        question: "How do permission levels work in team projects?",
        answer:
          "Project owners have full control, admins can manage members and settings, editors can create/modify content, and viewers have read-only access to shared resources.",
      },
      {
        question:
          "Can we create department-specific projects with different AI model access?",
        answer:
          "Yes, enterprise plans allow custom project configurations with specific AI model access, usage limits, and security policies per department.",
      },
      {
        question: "How does collaborative editing work in real-time?",
        answer:
          "Multiple team members can work on the same conversation or document simultaneously with change highlighting and automatic conflict resolution.",
      },
      {
        question:
          "Can we integrate Artifact with our company's existing training materials?",
        answer:
          "Yes, bulk import your training documents, create custom AI agents with your knowledge base, and use project memory to maintain company-specific context across all interactions.",
      },
    ],
  },
};
